import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RoomMovie } from "../../../api/room";
import { formatLikeCount, movieTitleHandler } from "../../../utils/helpers";
import styles from "./RoomMovieInfo.module.scss";

interface RoomMovieInfoProps {
  movie: RoomMovie;
  onLike: () => void;
  onDislike: () => void;
  onShare: () => void;
  isLiked: boolean;
  isSwiping: boolean;
}

const RoomMovieInfo: React.FC<RoomMovieInfoProps> = ({ movie, onLike, onDislike, onShare, isLiked, isSwiping }) => {
  const { t, i18n } = useTranslation();
  const [showOverview, setShowOverview] = useState(false);
  const rating = movie.imdb_rating?.toFixed(1) || movie.vote_average?.toFixed(1);
  const title = movieTitleHandler(i18n.language, movie) || movie.title;

  const getOverview = (): string => {
    const lng = i18n.language;
    if (lng === "ru" && movie.overview_ru) return movie.overview_ru;
    if (lng === "uk" && movie.overview_ua) return movie.overview_ua;
    if (lng === "ar" && movie.overview_ar) return movie.overview_ar;
    return movie.overview || "";
  };

  return (
    <div className={styles.infoBar}>
      <div className={styles.actions}>
        {/* Rating */}
        {rating && (
          <div className={styles.rating}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#f5c518">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <span className={styles.ratingValue}>{rating}</span>
          </div>
        )}

        {/* Like / Dislike buttons */}
        <div className={styles.buttonGroup}>
          <button
            className={`${styles.actionBtn} ${isLiked ? styles.actionBtnActive : ""}`}
            onClick={onLike}
            disabled={isSwiping}
            aria-label="Like"
          >
            <svg
              width="24"
              height="20"
              viewBox="0 0 24 24"
              fill={isLiked ? "#2ebd85" : "none"}
              stroke={isLiked ? "#2ebd85" : "currentColor"}
              strokeWidth="2"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span>{formatLikeCount(movie.likes_count, { compact: true })}</span>
          </button>

          <button className={styles.actionBtn} onClick={onDislike} disabled={isSwiping} aria-label="Dislike">
            <svg
              width="14"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            <span>{formatLikeCount(movie.shares_count, { compact: true })}</span>
          </button>

          <button className={styles.actionBtn} onClick={onShare} aria-label="Share">
            <svg width="24" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            <span>{formatLikeCount(movie.favorites_count, { compact: true })}</span>
          </button>
        </div>

        {/* Description button */}
        <button className={styles.descriptionBtn} onClick={() => setShowOverview(!showOverview)}>
          {t("room_description", "Description")}
        </button>
      </div>

      {/* Overview panel */}
      {showOverview && (
        <div className={styles.overview}>
          <h3 className={styles.overviewTitle}>{title}</h3>
          <p className={styles.overviewText}>{getOverview()}</p>
        </div>
      )}
    </div>
  );
};

export default RoomMovieInfo;
